/**
 * AgentsTab (Tab 2) — Team roster with editable records.
 *
 * Left:  searchable/filterable agent list with Team Member + upgrade badges
 * Right: profile header + DetailTabs [Overview / Commission / Edit / Plan]
 *
 * Business model (KOPS-465):
 * - Broker pays $500/mo for the Broker Portal tier
 * - Agents under that broker are FREE Team Members by default ($0/mo),
 *   with access to the shared Broker Portal features
 * - Agents can optionally upgrade their own subscription to unlock
 *   AI Search ($135/mo) or AI Pro ($185/mo) features
 *
 * Plan vocabulary is the new AISP taxonomy (team-member / ai-search /
 * ai-pro). The backend maps these to the legacy subscription_plan enum
 * values (essential / professional / premium) for DB compatibility
 * without requiring a schema migration.
 *
 * Backend:
 *   GET   /api/broker/agents/:brokerageId  → list (existing, real DB)
 *   PATCH /api/broker/agents/:agentId       → edit (KOPS-465 new)
 *   POST  /api/broker-invitations           → invite
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faPhone,
  faIdCard,
  faCircle,
  faUserPlus,
  faChartLine,
  faHandHoldingDollar,
  faCalendarAlt,
  faHistory,
  faPenToSquare,
  faFloppyDisk,
  faBuilding,
  faShieldAlt,
  faCrown,
  faStar,
} from '@fortawesome/free-solid-svg-icons';

import BrokerApiService from '../../../services/brokerApiService';
import axiosInstance from '../../../helpers/api';
import {
  SplitPanel,
  SearchBar,
  FilterPills,
  ListItem,
  ProfileHeader,
  NavyInput,
  NavySelect,
  GoldButton,
  OutlineButton,
  DetailTabs,
  colors,
  fonts,
} from '../shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * KOPS-465 team-plan taxonomy (4 tiers):
 *   team-member → Free under broker subscription ($0)
 *   agent-hub   → Individual CRM basics ($70)
 *   ai-search   → + AI-powered search ($135)
 *   ai-pro      → Full AI suite ($185)
 *
 * Source of truth: `team_plan` field returned by /api/broker/agents/:id
 * which is read from commission_settings.team_plan in brokerage_team_members.
 * Falls back to deriving from the legacy subscription_plan enum for rows
 * that haven't been touched since the KOPS-465 migration.
 */
type TeamPlanKey = 'team-member' | 'agent-hub' | 'ai-search' | 'ai-pro';

interface Agent {
  id: string;
  agent_full_name: string;
  agent_email?: string;
  agent_phone?: string;
  agent_license_number?: string;
  license_type?: 'BR' | 'SA';
  team_plan?: string;        // KOPS-465 authoritative field
  individual_plan?: string;  // legacy enum kept for backward compat
  monthly_fee?: number;
  is_active?: number | boolean;
  is_admin?: boolean;
  member_role?: string;
  member_status?: string;
  commission_split_agent?: number;
  commission_split_broker?: number;
  eo_fee?: number;
  transaction_fee?: number;
  last_activity?: string;
}

interface AgentsTabProps {
  brokerageId?: string;
}

// ---------------------------------------------------------------------------
// Plan mapping — the semantic bridge between legacy DB enum and AISP taxonomy
// ---------------------------------------------------------------------------

/**
 * Resolve an agent's team-plan tier. Prefers the authoritative team_plan
 * field from the backend (read from commission_settings.team_plan), then
 * falls back to deriving from the legacy subscription_plan enum.
 */
const resolveTeamPlan = (agent: Agent): TeamPlanKey => {
  const explicit = agent.team_plan;
  if (explicit === 'team-member' || explicit === 'agent-hub' ||
      explicit === 'ai-search' || explicit === 'ai-pro') {
    return explicit;
  }
  // Legacy fallback — old rows won't have team_plan set yet
  const legacy = agent.individual_plan;
  if (legacy === 'professional') return 'ai-search';
  if (legacy === 'premium') return 'ai-pro';
  // If fee is ~70 but enum is essential, it's probably agent-hub
  if (agent.monthly_fee && Math.round(agent.monthly_fee) === 70) return 'agent-hub';
  return 'team-member';
};

const TEAM_PLAN_META: Record<TeamPlanKey, {
  label: string;
  shortLabel: string;
  tagline: string;
  price: number;
  color: string;
  icon: typeof faShieldAlt;
}> = {
  'team-member': {
    label: 'Team Member',
    shortLabel: 'TEAM',
    tagline: 'Free under broker subscription',
    price: 0,
    color: colors.green,
    icon: faShieldAlt,
  },
  'agent-hub': {
    label: 'Agent Hub',
    shortLabel: 'AGENT HUB',
    tagline: 'Individual CRM basics — lead management + buyer portal',
    price: 70,
    color: colors.muted,
    icon: faUser,
  },
  'ai-search': {
    label: 'AI Search',
    shortLabel: 'AI SEARCH',
    tagline: 'Adds AI-powered search + individual CRM features',
    price: 135,
    color: colors.blue,
    icon: faStar,
  },
  'ai-pro': {
    label: 'AI Pro',
    shortLabel: 'AI PRO',
    tagline: 'Full AI suite + advanced automation',
    price: 185,
    color: colors.gold,
    icon: faCrown,
  },
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { key: 'all',      label: 'All' },
  { key: 'active',   label: 'Active' },
  { key: 'pending',  label: 'Pending' },
  { key: 'inactive', label: 'Inactive' },
];

const PLAN_OPTIONS = [
  { key: 'all',         label: 'All Plans' },
  { key: 'team-member', label: 'Team Only' },
  { key: 'agent-hub',   label: 'Agent Hub' },
  { key: 'ai-search',   label: 'AI Search' },
  { key: 'ai-pro',      label: 'AI Pro' },
];

const DETAIL_TABS = [
  { key: 'overview',   label: 'Overview',   icon: faUser },
  { key: 'edit',       label: 'Edit',       icon: faPenToSquare },
  { key: 'plan',       label: 'Plan',       icon: faChartLine },
  { key: 'commission', label: 'Commission', icon: faHandHoldingDollar },
  { key: 'activity',   label: 'Activity',   icon: faHistory },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AgentsTab: React.FC<AgentsTabProps> = ({ brokerageId }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [selected, setSelected] = useState<Agent | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');

  // Invite + Edit state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '', phone: '' });
  const [inviting, setInviting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [planChanging, setPlanChanging] = useState(false);
  const [resending, setResending] = useState(false);
  const [actionAlert, setActionAlert] = useState<string | null>(null);

  // ---- Fetch ---------------------------------------------------------------
  const fetchAgents = useCallback(async () => {
    if (!brokerageId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await BrokerApiService.getAgents(brokerageId);
      setAgents(Array.isArray(list) ? (list as Agent[]) : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [brokerageId]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // ---- Derived: filter + search -------------------------------------------
  const filteredAgents = useMemo(() => {
    return agents.filter((a) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const hit =
          a.agent_full_name?.toLowerCase().includes(q) ||
          a.agent_email?.toLowerCase().includes(q) ||
          a.agent_license_number?.toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (statusFilter === 'active' && a.member_status !== 'active' && !a.is_active) return false;
      if (statusFilter === 'pending' && a.member_status !== 'pending') return false;
      if (statusFilter === 'inactive' && a.member_status !== 'inactive' && a.is_active) return false;
      if (planFilter !== 'all' && resolveTeamPlan(a) !== planFilter) return false;
      return true;
    });
  }, [agents, search, statusFilter, planFilter]);

  // ---- Stats ---------------------------------------------------------------
  const stats = useMemo(() => {
    const total = agents.length;
    const active = agents.filter((a) => a.member_status === 'active' || a.is_active).length;
    const teamOnly = agents.filter((a) => resolveTeamPlan(a) === 'team-member').length;
    const upgraded = agents.filter((a) => resolveTeamPlan(a) !== 'team-member').length;
    return { total, active, teamOnly, upgraded };
  }, [agents]);

  // ---- Actions -------------------------------------------------------------
  // Invite flow — correct existing endpoint is POST /broker/invitations/save-agent
  // (NOT /broker-invitations which never existed and 404'd). Payload shape
  // is { agentData: {...}, brokerageId } per brokerage-invitation.controller.js
  // saveBrokerageAgent. After save succeeds we also trigger the invitation
  // email via /broker/invitations/send-invitation so the agent gets an
  // activation link — that's the canonical two-step flow from the legacy
  // broker-portal Agents page.
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteData.email || !inviteData.name) {
      setActionAlert('❌ Name and email required');
      setTimeout(() => setActionAlert(null), 3000);
      return;
    }
    setInviting(true);
    try {
      // Split the "name" field into first/last for the backend schema
      const parts = inviteData.name.trim().split(/\s+/);
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ') || '';

      const email = inviteData.email.trim();
      const agentData = {
        firstName,
        lastName,
        email,
        // sendBrokerageInvitation expects snake_case agent_email — keep
        // both keys so the save-agent + send-invitation endpoints can
        // each read what they expect without a mid-flight translation.
        agent_email: email,
        phone: inviteData.phone || null,
        licenseType: 'SA',
        plan: 'essential',
      };

      // Step 1: create the brokerage_team_members row. The backend now
      // eagerly links user_id if the invitee already has a Users row
      // (KOPS-465 fix) so MLS inheritance + the Brokerage Relationship
      // card work immediately for already-registered users.
      const saveRes = await axiosInstance.post('/broker/invitations/save-agent', {
        agentData,
        brokerageId,
      });

      // Step 2: only send the activation email for TRULY NEW users.
      // If the invitee was already in Users (eager-link branch), their
      // row is already member_status='active' with user_id populated —
      // sending an email would reset them to 'pending' and require a
      // useless activation click for someone who already has an account.
      const member = saveRes.data?.member;
      const needsEmail = saveRes.data?.success && member?.needs_invitation_email;

      if (needsEmail) {
        try {
          await axiosInstance.post('/broker/invitations/send-invitation', {
            memberId: member.id,
            agentData,
            brokerageData: { brokerage_name: 'Your Brokerage' },
          });
        } catch (emailErr) {
          console.warn('Invite email step failed (row saved, can resend)', emailErr);
        }
      }

      setActionAlert(
        needsEmail
          ? '✅ Invitation email sent — waiting for agent to accept'
          : '✅ Agent linked to brokerage (already had an account)'
      );
      setShowInviteForm(false);
      setInviteData({ name: '', email: '', phone: '' });
      await fetchAgents();
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Invite failed'}`);
    } finally {
      setInviting(false);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  const patchAgent = async (agent: Agent, payload: Record<string, any>) => {
    const res = await axiosInstance.patch(`/broker/agents/${agent.id}`, payload);
    return res.data;
  };

  // Resend invitation — calls /broker/invitations/send-invitation with
  // the existing memberId. The backend generates a new activation token
  // (the old one is overwritten) and re-sends the navy+gold email. Used
  // when an agent reports they never received the original invite — no
  // need to delete + re-invite them.
  const handleResendInvite = async (agent: Agent) => {
    if (!agent?.id) return;
    setResending(true);
    try {
      const fullName = agent.agent_full_name || '';
      const parts = fullName.trim().split(/\s+/);
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ') || '';
      const email = agent.agent_email || '';

      await axiosInstance.post('/broker/invitations/send-invitation', {
        memberId: agent.id,
        agentData: {
          firstName,
          lastName,
          email,
          agent_email: email, // backend reads agent_email (snake_case)
          phone: agent.agent_phone || null,
          licenseType: agent.license_type || 'SA',
        },
        brokerageData: { brokerage_name: 'Your Brokerage' },
      });
      setActionAlert(`✅ Invitation email resent to ${email}`);
      await fetchAgents();
    } catch (err: any) {
      setActionAlert(
        `❌ Resend failed: ${err?.response?.data?.error || err?.message || 'unknown error'}`
      );
    } finally {
      setResending(false);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  const handleSaveEdit = async (agent: Agent, payload: Record<string, any>) => {
    setSavingEdit(true);
    try {
      const result = await patchAgent(agent, payload);
      if (result?.success) {
        setActionAlert('✅ Agent updated');
        await fetchAgents();
        // Keep the selected row fresh with the server response
        if (result.agent) {
          setSelected((prev) =>
            prev
              ? {
                  ...prev,
                  agent_full_name: `${result.agent.invite_first_name || ''} ${result.agent.invite_last_name || ''}`.trim() || prev.agent_full_name,
                  agent_email: result.agent.invite_email || prev.agent_email,
                  agent_phone: result.agent.invite_phone || prev.agent_phone,
                  agent_license_number: result.agent.invite_license_number || prev.agent_license_number,
                  license_type: result.agent.invite_license_type || prev.license_type,
                  member_role: result.agent.member_role || prev.member_role,
                  member_status: result.agent.member_status || prev.member_status,
                  team_plan: result.agent.team_plan || prev.team_plan,
                  individual_plan: result.agent.subscription_plan || prev.individual_plan,
                  monthly_fee: result.agent.monthly_fee ?? prev.monthly_fee,
                  commission_split_agent: result.agent.commission_split_agent ?? prev.commission_split_agent,
                  commission_split_broker: result.agent.commission_split_broker ?? prev.commission_split_broker,
                  eo_fee: result.agent.eo_fee ?? prev.eo_fee,
                  transaction_fee: result.agent.transaction_fee ?? prev.transaction_fee,
                  is_admin: result.agent.is_admin ?? prev.is_admin,
                }
              : prev
          );
        }
        return true;
      } else {
        setActionAlert(`❌ ${result?.error || 'Update failed'}`);
        return false;
      }
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Update failed'}`);
      return false;
    } finally {
      setSavingEdit(false);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  const handlePlanChange = async (agent: Agent, newPlan: TeamPlanKey) => {
    setPlanChanging(true);
    try {
      // Send as team_plan (new 4-tier field). Backend also accepts
      // subscription_plan for backward compat but team_plan is preferred.
      await patchAgent(agent, { team_plan: newPlan });
      setActionAlert(`✅ Plan changed to ${TEAM_PLAN_META[newPlan].label}`);
      await fetchAgents();
      // Optimistically update selected row so the UI reflects the change
      // before the fetchAgents round-trip settles.
      setSelected((prev) => (prev ? { ...prev, team_plan: newPlan } : prev));
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Plan update failed'}`);
    } finally {
      setPlanChanging(false);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  // ---- Render --------------------------------------------------------------
  return (
    <>
      <StatsBar stats={stats} loading={loading} />

      {actionAlert && (
        <div
          style={{
            background: actionAlert.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: actionAlert.startsWith('✅') ? colors.green : colors.red,
            padding: '8px 24px',
            borderBottom: `1px solid ${colors.border}`,
            fontSize: '13px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {actionAlert}
        </div>
      )}

      <SplitPanel
        left={
          <>
            <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search agents..." />
                <GoldButton
                  onClick={() => setShowInviteForm((v) => !v)}
                  style={{ whiteSpace: 'nowrap', padding: '8px 12px' }}
                >
                  {showInviteForm ? '✕' : '+ Invite'}
                </GoldButton>
              </div>

              {showInviteForm && (
                <form
                  onSubmit={handleInvite}
                  style={{
                    background: colors.navy3,
                    borderRadius: '8px',
                    padding: '10px',
                    marginBottom: '8px',
                  }}
                >
                  <NavyInput
                    required
                    placeholder="Full name"
                    value={inviteData.name}
                    onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                    style={{ marginBottom: '6px', background: colors.navy2 }}
                  />
                  <NavyInput
                    required
                    type="email"
                    placeholder="Email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    style={{ marginBottom: '6px', background: colors.navy2 }}
                  />
                  <NavyInput
                    placeholder="Phone (optional)"
                    value={inviteData.phone}
                    onChange={(e) => setInviteData({ ...inviteData, phone: e.target.value })}
                    style={{ marginBottom: '8px', background: colors.navy2 }}
                  />
                  <GoldButton type="submit" disabled={inviting} style={{ width: '100%' }}>
                    {inviting ? 'Sending…' : 'Send Invitation'}
                  </GoldButton>
                </form>
              )}

              <div style={{ marginBottom: '6px' }}>
                <FilterPills options={STATUS_OPTIONS} activeKey={statusFilter} onChange={setStatusFilter} />
              </div>
              <FilterPills options={PLAN_OPTIONS} activeKey={planFilter} onChange={setPlanFilter} />
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>
                  Loading agents…
                </div>
              ) : error ? (
                <div style={{ padding: '20px', color: colors.red, fontSize: '12px' }}>{error}</div>
              ) : filteredAgents.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                  <FontAwesomeIcon icon={faUserPlus} style={{ fontSize: '32px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '13px' }}>
                    {agents.length === 0
                      ? 'No agents yet. Invite your first team member!'
                      : 'No agents match current filters'}
                  </div>
                </div>
              ) : (
                filteredAgents.map((a) => (
                  <ListItem
                    key={a.id}
                    selected={selected?.id === a.id}
                    onClick={() => {
                      setSelected(a);
                      setActiveDetailTab('overview');
                    }}
                    title={a.agent_full_name || '(unnamed)'}
                    subtitle={a.agent_email}
                    meta={
                      <span>
                        <FontAwesomeIcon
                          icon={faCircle}
                          style={{
                            color: a.member_status === 'active' || a.is_active ? colors.green : colors.muted,
                            fontSize: '6px',
                            marginRight: '6px',
                          }}
                        />
                        {a.member_status === 'active' || a.is_active ? 'Active' : a.member_status || 'Inactive'}
                        {a.is_admin && (
                          <span style={{ marginLeft: '8px', color: colors.gold2, fontSize: '10px', fontWeight: 700 }}>
                            <FontAwesomeIcon icon={faCrown} /> ADMIN
                          </span>
                        )}
                      </span>
                    }
                    rightContent={<TeamPlanChip plan={resolveTeamPlan(a)} compact />}
                  />
                ))
              )}
            </div>
          </>
        }
        right={
          selected ? (
            <AgentDetail
              agent={selected}
              brokerageId={brokerageId}
              activeTab={activeDetailTab}
              onTabChange={setActiveDetailTab}
              savingEdit={savingEdit}
              planChanging={planChanging}
              resending={resending}
              onSaveEdit={(payload) => handleSaveEdit(selected, payload)}
              onPlanChange={(plan) => handlePlanChange(selected, plan)}
              onResendInvite={() => handleResendInvite(selected)}
              onRefresh={fetchAgents}
            />
          ) : (
            <EmptyDetailState />
          )
        }
      />
    </>
  );
};

// ===========================================================================
// Stats bar
// ===========================================================================

const StatsBar: React.FC<{ stats: { total: number; active: number; teamOnly: number; upgraded: number }; loading: boolean }> = ({ stats, loading }) => (
  <div
    style={{
      background: colors.offBg,
      borderBottom: `1px solid ${colors.border}`,
      padding: '16px 24px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '12px',
      flexShrink: 0,
    }}
  >
    <StatCard icon={faUser}       label="Total Agents" value={loading ? '—' : stats.total}    color={colors.blue} />
    <StatCard icon={faCircle}     label="Active"       value={loading ? '—' : stats.active}   color={colors.green} />
    <StatCard icon={faShieldAlt}  label="Team Only"    value={loading ? '—' : stats.teamOnly} color={colors.muted} />
    <StatCard icon={faCrown}      label="Upgraded"     value={loading ? '—' : stats.upgraded} color={colors.gold} />
  </div>
);

const StatCard: React.FC<{ icon: any; label: string; value: React.ReactNode; color: string }> = ({ icon, label, value, color }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: '8px',
      padding: '14px 18px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    }}
  >
    <div
      style={{
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: `${color}22`,
        border: `2px solid ${color}55`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: '14px' }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: fonts.display, fontSize: '24px', color: colors.navy, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: colors.navy, marginTop: '2px' }}>{label}</div>
    </div>
  </div>
);

// ===========================================================================
// Right panel — agent detail + sub-tabs
// ===========================================================================

interface AgentDetailProps {
  agent: Agent;
  brokerageId?: string;
  activeTab: string;
  onTabChange: (k: string) => void;
  savingEdit: boolean;
  planChanging: boolean;
  resending: boolean;
  onSaveEdit: (payload: Record<string, any>) => Promise<boolean>;
  onPlanChange: (plan: TeamPlanKey) => void;
  onResendInvite: () => void;
  onRefresh: () => Promise<void>;
}

const AgentDetail: React.FC<AgentDetailProps> = ({
  agent,
  brokerageId,
  activeTab,
  onTabChange,
  savingEdit,
  planChanging,
  resending,
  onSaveEdit,
  onPlanChange,
  onResendInvite,
  onRefresh,
}) => {
  // Resend is available for anyone who isn't currently active:
  //   - 'pending'  → agent hasn't accepted the original invite yet
  //   - 'inactive' → agent was deactivated, broker wants to re-engage
  //   - ''         → legacy orphan rows with blank status (KOPS-465
  //                  data bug — Raquel / Sharonda / Michael Harris)
  //   - undefined  → row exists but status wasn't returned
  // Active agents never see the resend button — they're already in.
  const status = agent.member_status || '';
  const canResend = status === 'pending' || status === 'inactive' || status === '';
  const teamPlan = resolveTeamPlan(agent);

  // KOPS-465 — "Disable Access" / "Reactivate" state machine. When an
  // agent leaves for another brokerage, the broker hits Disable Access
  // and the agent's member_status flips to 'inactive' — effective
  // immediately. An inline confirmation prompt (navy2 card) prevents
  // misclicks and captures a termination reason that gets stored on
  // the team-member row for audit.
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disableReason, setDisableReason] = useState('Left for another brokerage');
  const [disableNotes, setDisableNotes] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [accessActionResult, setAccessActionResult] = useState<string | null>(null);

  const handleDisableAccess = async () => {
    setDisabling(true);
    setAccessActionResult(null);
    try {
      const res = await axiosInstance.post(`/broker/agents/${agent.id}/terminate`, {
        terminationReason: disableReason || 'Administrative Action',
        terminationNotes: disableNotes || null,
      });
      if (res.data?.success) {
        setAccessActionResult(`✅ Access disabled for ${agent.agent_full_name || 'agent'}`);
        setShowDisableConfirm(false);
        setDisableNotes('');
        await onRefresh();
      } else {
        setAccessActionResult(
          `❌ ${res.data?.message || res.data?.error || 'Failed to disable access'}`
        );
      }
    } catch (err: any) {
      setAccessActionResult(
        `❌ ${err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to disable access'}`
      );
    } finally {
      setDisabling(false);
      setTimeout(() => setAccessActionResult(null), 5000);
    }
  };

  const handleReactivate = async () => {
    setDisabling(true);
    setAccessActionResult(null);
    try {
      const res = await axiosInstance.post(`/broker/agents/${agent.id}/reactivate`, {});
      if (res.data?.success) {
        setAccessActionResult(`✅ ${agent.agent_full_name || 'Agent'} access restored`);
        await onRefresh();
      } else {
        setAccessActionResult(
          `❌ ${res.data?.message || res.data?.error || 'Failed to reactivate'}`
        );
      }
    } catch (err: any) {
      setAccessActionResult(
        `❌ ${err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to reactivate'}`
      );
    } finally {
      setDisabling(false);
      setTimeout(() => setAccessActionResult(null), 5000);
    }
  };

  return (
    <div>
      <ProfileHeader
        title={agent.agent_full_name || '(unnamed agent)'}
        metaItems={[
          { icon: faEnvelope, text: agent.agent_email || '—' },
          ...(agent.agent_phone ? [{ icon: faPhone, text: agent.agent_phone }] : []),
          ...(agent.agent_license_number
            ? [{ icon: faIdCard, text: `${agent.license_type || 'License'} ${agent.agent_license_number}` }]
            : []),
        ]}
        badges={
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <TeamPlanChip plan="team-member" compact alwaysShow />
            {teamPlan !== 'team-member' && <TeamPlanChip plan={teamPlan} compact />}
            {agent.is_admin && (
              <span
                style={{
                  background: `${colors.gold}22`,
                  color: colors.gold,
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                <FontAwesomeIcon icon={faCrown} /> Admin
              </span>
            )}
            <ActiveBadge active={agent.member_status === 'active' || !!agent.is_active} status={agent.member_status} />
          </div>
        }
      />

      {/* ---- Access action row ----
          Disable Access = flip member_status → 'inactive' immediately.
          Reactivate     = flip back to 'active' (for agents who came back).
          Both write to the same brokerage_team_members row that the
          entire hub reads from, so every other tab + the Activity feed
          reflect the change on refresh. */}
      <div
        style={{
          margin: '0 24px 12px 24px',
          display: 'flex',
          gap: '8px',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {status === 'active' ? (
          <button
            onClick={() => setShowDisableConfirm((v) => !v)}
            disabled={disabling}
            style={{
              background: 'transparent',
              color: colors.red,
              border: `1px solid ${colors.red}`,
              borderRadius: '6px',
              padding: '8px 14px',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              cursor: disabling ? 'not-allowed' : 'pointer',
            }}
          >
            🚫 {showDisableConfirm ? 'Cancel' : 'Disable Access'}
          </button>
        ) : status === 'inactive' ? (
          <button
            onClick={handleReactivate}
            disabled={disabling}
            style={{
              background: 'transparent',
              color: colors.green,
              border: `1px solid ${colors.green}`,
              borderRadius: '6px',
              padding: '8px 14px',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              cursor: disabling ? 'not-allowed' : 'pointer',
            }}
          >
            {disabling ? 'Working…' : '✓ Reactivate Access'}
          </button>
        ) : null}
      </div>

      {accessActionResult && (
        <div
          style={{
            margin: '0 24px 12px 24px',
            padding: '10px 14px',
            background: accessActionResult.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: accessActionResult.startsWith('✅') ? colors.green : colors.red,
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {accessActionResult}
        </div>
      )}

      {showDisableConfirm && status === 'active' && (
        <div
          style={{
            margin: '0 24px 16px 24px',
            padding: '16px 18px',
            background: `${colors.red}0a`,
            border: `1px solid ${colors.red}44`,
            borderLeft: `4px solid ${colors.red}`,
            borderRadius: '8px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '4px' }}>
            Disable {agent.agent_full_name || 'this agent'}'s access?
          </div>
          <div style={{ fontSize: '11px', color: colors.muted, marginBottom: '12px', lineHeight: 1.5 }}>
            The agent's status flips to <strong>inactive</strong> immediately.
            They'll no longer see the brokerage in their portal or be able to
            submit documents. You can reactivate them later from this same
            screen if they come back.
          </div>

          <div style={{ marginBottom: '10px' }}>
            <div
              style={{
                fontSize: '10px',
                color: colors.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                marginBottom: '4px',
              }}
            >
              Reason
            </div>
            <select
              value={disableReason}
              onChange={(e) => setDisableReason(e.target.value)}
              style={{
                width: '100%',
                background: colors.white,
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                padding: '8px 10px',
                fontSize: '12px',
                color: colors.navy,
                fontFamily: 'inherit',
              }}
            >
              <option value="Left for another brokerage">Left for another brokerage</option>
              <option value="Voluntary Resignation">Voluntary resignation</option>
              <option value="Terminated">Terminated</option>
              <option value="License expired">License expired</option>
              <option value="Retired">Retired</option>
              <option value="Administrative Action">Administrative action</option>
            </select>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div
              style={{
                fontSize: '10px',
                color: colors.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                marginBottom: '4px',
              }}
            >
              Notes (optional)
            </div>
            <textarea
              value={disableNotes}
              onChange={(e) => setDisableNotes(e.target.value)}
              placeholder="e.g. Joining Realty One on 4/15"
              rows={2}
              style={{
                width: '100%',
                background: colors.white,
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                padding: '8px 10px',
                fontSize: '12px',
                color: colors.navy,
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowDisableConfirm(false);
                setDisableNotes('');
              }}
              disabled={disabling}
              style={{
                background: 'transparent',
                color: colors.muted,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: disabling ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDisableAccess}
              disabled={disabling}
              style={{
                background: disabling ? colors.muted : colors.red,
                color: colors.white,
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: disabling ? 'not-allowed' : 'pointer',
              }}
            >
              {disabling ? 'Disabling…' : '🚫 Confirm Disable'}
            </button>
          </div>
        </div>
      )}

      {/* Resend-invitation banner — visible for any non-active agent.
          Lets the broker send a fresh activation email without having
          to delete + re-invite. Calls POST /broker/invitations/send-invitation
          which rotates the activation token and fires a fresh
          navy+gold email. Status-aware copy tells the broker what
          state the agent is actually in. */}
      {canResend && (
        <div
          style={{
            margin: '0 24px 16px 24px',
            padding: '14px 18px',
            background: `${colors.gold}0a`,
            border: `1px solid ${colors.gold}44`,
            borderLeft: `4px solid ${colors.gold}`,
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '2px' }}>
              {status === 'pending'
                ? 'Invitation pending'
                : status === 'inactive'
                ? 'Agent inactive — re-engage with a new invite'
                : 'Agent not yet linked to an account'}
            </div>
            <div style={{ fontSize: '11px', color: colors.muted }}>
              {status === 'pending' &&
                `${agent.agent_email || 'Agent'} hasn't accepted yet. Didn't receive the email? Resend it.`}
              {status === 'inactive' &&
                `${agent.agent_email || 'Agent'} was deactivated. Sending a new invitation will re-activate their access on acceptance.`}
              {status !== 'pending' &&
                status !== 'inactive' &&
                `${agent.agent_email || 'Agent'} has no account linked yet. Send an invitation so they can join the brokerage.`}
            </div>
          </div>
          <button
            onClick={onResendInvite}
            disabled={resending}
            style={{
              background: resending ? colors.muted : colors.gold,
              color: colors.navy,
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              cursor: resending ? 'not-allowed' : 'pointer',
              flexShrink: 0,
            }}
          >
            {resending
              ? 'Sending…'
              : status === 'pending'
              ? '📧 Resend Invitation'
              : '📧 Send Invitation'}
          </button>
        </div>
      )}

      <DetailTabs tabs={DETAIL_TABS} activeTab={activeTab} onChange={onTabChange} />

      <div style={{ padding: '0 24px 24px 24px' }}>
        {activeTab === 'overview' && <OverviewPane agent={agent} />}
        {activeTab === 'edit' && <EditPane agent={agent} saving={savingEdit} onSave={onSaveEdit} />}
        {activeTab === 'plan' && <PlanPane agent={agent} planChanging={planChanging} onPlanChange={onPlanChange} />}
        {activeTab === 'commission' && (
          <CommissionPane
            agent={agent}
            brokerageId={brokerageId}
            saving={savingEdit}
            onSave={onSaveEdit}
            onRefresh={onRefresh}
          />
        )}
        {activeTab === 'activity' && <ActivityPane agent={agent} />}
      </div>
    </div>
  );
};

// ---- Panes -----------------------------------------------------------------

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '12px',
    }}
  >
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        color: colors.navy,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '10px',
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const KeyValue: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '6px 0',
      borderBottom: `1px solid ${colors.border}`,
      fontSize: '13px',
    }}
  >
    <span style={{ color: colors.muted }}>{k}</span>
    <span style={{ color: colors.navy, fontWeight: 500, textAlign: 'right' }}>{v}</span>
  </div>
);

const FormLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label
    style={{
      display: 'block',
      fontSize: '11px',
      fontWeight: 600,
      color: colors.muted,
      marginBottom: '5px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}
  >
    {children}
  </label>
);

const WhiteInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <NavyInput
    {...props}
    style={{
      background: colors.off,
      color: colors.navy,
      border: `1px solid ${colors.border}`,
      ...props.style,
    }}
  />
);

const WhiteSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { children: React.ReactNode }> = ({ children, ...props }) => (
  <NavySelect
    {...props}
    style={{
      background: colors.off,
      color: colors.navy,
      border: `1px solid ${colors.border}`,
      ...props.style,
    }}
  >
    {children}
  </NavySelect>
);

// ---- Overview pane ---------------------------------------------------------

const OverviewPane: React.FC<{ agent: Agent }> = ({ agent }) => {
  const teamPlan = resolveTeamPlan(agent);
  const meta = TEAM_PLAN_META[teamPlan];
  return (
    <>
      <div
        style={{
          background: `${meta.color}11`,
          border: `1px solid ${meta.color}44`,
          borderLeft: `4px solid ${meta.color}`,
          borderRadius: '8px',
          padding: '14px 18px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
        }}
      >
        <div
          style={{
            width: '42px',
            height: '42px',
            borderRadius: '50%',
            background: `${meta.color}22`,
            border: `2px solid ${meta.color}66`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={meta.icon} style={{ color: meta.color, fontSize: '18px' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>
            {meta.label} {teamPlan === 'team-member' ? '· Free' : `· $${meta.price}/mo`}
          </div>
          <div style={{ fontSize: '12px', color: colors.muted }}>{meta.tagline}</div>
        </div>
      </div>

      <SectionCard title="Contact & License">
        <KeyValue k="Full name" v={agent.agent_full_name || '—'} />
        <KeyValue k="Email" v={agent.agent_email || '—'} />
        <KeyValue k="Phone" v={agent.agent_phone || '—'} />
        <KeyValue k="License #" v={agent.agent_license_number || '—'} />
        <KeyValue k="License type" v={agent.license_type || '—'} />
        <KeyValue k="Role" v={agent.is_admin ? 'Admin' : (agent.member_role || 'Agent')} />
        <KeyValue k="Status" v={<ActiveBadge active={agent.member_status === 'active' || !!agent.is_active} status={agent.member_status} />} />
      </SectionCard>
    </>
  );
};

// ---- Edit pane -------------------------------------------------------------

const EditPane: React.FC<{
  agent: Agent;
  saving: boolean;
  onSave: (payload: Record<string, any>) => Promise<boolean>;
}> = ({ agent, saving, onSave }) => {
  // Split agent_full_name into first/last for the form
  const [first, last] = (() => {
    const name = agent.agent_full_name?.replace(/\s*\(Pending Invitation\)\s*$/, '') || '';
    const parts = name.trim().split(/\s+/);
    return [parts[0] || '', parts.slice(1).join(' ') || ''];
  })();

  const [form, setForm] = useState({
    invite_first_name: first,
    invite_last_name: last,
    invite_email: agent.agent_email || '',
    invite_phone: agent.agent_phone || '',
    invite_license_number: agent.agent_license_number || '',
    invite_license_type: agent.license_type || 'SA',
    member_status: agent.member_status || 'active',
    is_admin: !!agent.is_admin,
  });

  // Reset form when the selected agent changes
  useEffect(() => {
    const name = agent.agent_full_name?.replace(/\s*\(Pending Invitation\)\s*$/, '') || '';
    const parts = name.trim().split(/\s+/);
    setForm({
      invite_first_name: parts[0] || '',
      invite_last_name: parts.slice(1).join(' ') || '',
      invite_email: agent.agent_email || '',
      invite_phone: agent.agent_phone || '',
      invite_license_number: agent.agent_license_number || '',
      invite_license_type: agent.license_type || 'SA',
      member_status: agent.member_status || 'active',
      is_admin: !!agent.is_admin,
    });
  }, [agent.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <SectionCard title="Contact Info">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div>
            <FormLabel>First name</FormLabel>
            <WhiteInput
              value={form.invite_first_name}
              onChange={(e) => update('invite_first_name', e.target.value)}
              placeholder="Jane"
            />
          </div>
          <div>
            <FormLabel>Last name</FormLabel>
            <WhiteInput
              value={form.invite_last_name}
              onChange={(e) => update('invite_last_name', e.target.value)}
              placeholder="Doe"
            />
          </div>
        </div>
        <FormLabel>Email</FormLabel>
        <WhiteInput
          type="email"
          value={form.invite_email}
          onChange={(e) => update('invite_email', e.target.value)}
          placeholder="jane@brokerage.com"
          style={{ marginBottom: '12px' }}
        />
        <FormLabel>Phone</FormLabel>
        <WhiteInput
          value={form.invite_phone}
          onChange={(e) => update('invite_phone', e.target.value)}
          placeholder="(555) 123-4567"
        />
      </SectionCard>

      <SectionCard title="License">
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
          <div>
            <FormLabel>License #</FormLabel>
            <WhiteInput
              value={form.invite_license_number}
              onChange={(e) => update('invite_license_number', e.target.value)}
              placeholder="SA-123456"
            />
          </div>
          <div>
            <FormLabel>Type</FormLabel>
            <WhiteSelect
              value={form.invite_license_type}
              onChange={(e) => update('invite_license_type', e.target.value as 'BR' | 'SA')}
            >
              <option value="SA">SA — Salesperson</option>
              <option value="BR">BR — Broker</option>
            </WhiteSelect>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Role & Status">
        <FormLabel>Member Status</FormLabel>
        <WhiteSelect
          value={form.member_status}
          onChange={(e) => update('member_status', e.target.value)}
          style={{ marginBottom: '14px' }}
        >
          <option value="active">Active</option>
          <option value="pending">Pending Invitation</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </WhiteSelect>

        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.is_admin}
            onChange={(e) => update('is_admin', e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <span style={{ fontSize: '13px', color: colors.navy, fontWeight: 500 }}>
            Grant brokerage admin privileges
          </span>
        </label>
      </SectionCard>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <GoldButton type="submit" disabled={saving}>
          <FontAwesomeIcon icon={faFloppyDisk} /> {saving ? 'Saving…' : 'Save Changes'}
        </GoldButton>
      </div>
    </form>
  );
};

// ---- Plan pane -------------------------------------------------------------
// KOPS-465 billing boundary:
//   - team-member + agent-hub → BROKER-PAID (direct Switch button)
//   - ai-search + ai-pro     → AGENT-PAID (Send Invite button — agent self-serves)

const BROKER_PAID_PLANS = new Set<TeamPlanKey>(['team-member', 'agent-hub']);

const PlanPane: React.FC<{
  agent: Agent;
  planChanging: boolean;
  onPlanChange: (plan: TeamPlanKey) => void;
}> = ({ agent, planChanging, onPlanChange }) => {
  const currentPlan = resolveTeamPlan(agent);

  const [showInviteForm, setShowInviteForm] = useState<TeamPlanKey | null>(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);

  const handleSendInvite = async (target: TeamPlanKey) => {
    setInviteSending(true);
    setInviteResult(null);
    try {
      const res = await axiosInstance.post(`/broker/agents/${agent.id}/upgrade-invite`, {
        target_plan: target,
        message: inviteMessage || null,
      });
      if (res.data?.success) {
        setInviteResult(`✅ Invitation for ${TEAM_PLAN_META[target].label} sent`);
        setShowInviteForm(null);
        setInviteMessage('');
      } else {
        setInviteResult(`❌ ${res.data?.error || 'Invite failed'}`);
      }
    } catch (err: any) {
      setInviteResult(`❌ ${err?.response?.data?.error || err?.message || 'Invite failed'}`);
    } finally {
      setInviteSending(false);
      setTimeout(() => setInviteResult(null), 5000);
    }
  };

  return (
    <>
      <div
        style={{
          background: `${colors.green}11`,
          border: `1px solid ${colors.green}44`,
          borderLeft: `3px solid ${colors.green}`,
          borderRadius: '6px',
          padding: '12px 14px',
          fontSize: '12px',
          color: '#555',
          lineHeight: 1.5,
          marginBottom: '12px',
        }}
      >
        <FontAwesomeIcon icon={faBuilding} style={{ color: colors.green, marginRight: '6px' }} />
        <strong>Team Member</strong> and <strong>Agent Hub</strong> are included in your Broker
        Portal subscription — you can assign them directly.{' '}
        <strong>AI Search</strong> and <strong>AI Pro</strong> are personal upgrades that each
        agent pays from their own account. Click <em>Send Upgrade Invite</em> and the agent can
        self-serve from their dashboard.
      </div>

      {inviteResult && (
        <div
          style={{
            background: inviteResult.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: inviteResult.startsWith('✅') ? colors.green : colors.red,
            padding: '10px 14px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            marginBottom: '12px',
          }}
        >
          {inviteResult}
        </div>
      )}

      <SectionCard title="Available Plans">
        {(['team-member', 'agent-hub', 'ai-search', 'ai-pro'] as TeamPlanKey[]).map((key) => {
          const meta = TEAM_PLAN_META[key];
          const isCurrent = key === currentPlan;
          const isBrokerPaid = BROKER_PAID_PLANS.has(key);
          const isInviteOpen = showInviteForm === key;

          return (
            <div
              key={key}
              style={{
                border: `1px solid ${isCurrent ? meta.color : colors.border}`,
                borderRadius: '8px',
                marginBottom: '10px',
                background: isCurrent ? `${meta.color}11` : colors.white,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px',
                }}
              >
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    background: `${meta.color}22`,
                    border: `2px solid ${meta.color}66`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FontAwesomeIcon icon={meta.icon} style={{ color: meta.color, fontSize: '18px' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>
                    {meta.label}{' '}
                    <span style={{ color: meta.color }}>
                      {key === 'team-member' ? '· Free' : `· $${meta.price}/mo`}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: colors.muted }}>{meta.tagline}</div>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: isBrokerPaid ? colors.gold : colors.blue,
                      marginTop: '3px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {isBrokerPaid ? '🏢 Billed to broker' : '👤 Billed to agent'}
                  </div>
                </div>
                {isCurrent ? (
                  <span
                    style={{
                      background: meta.color,
                      color: colors.white,
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      flexShrink: 0,
                    }}
                  >
                    Current
                  </span>
                ) : isBrokerPaid ? (
                  <OutlineButton onClick={() => onPlanChange(key)} disabled={planChanging}>
                    {planChanging ? '…' : 'Switch'}
                  </OutlineButton>
                ) : (
                  <OutlineButton
                    onClick={() =>
                      setShowInviteForm((cur) => (cur === key ? null : key))
                    }
                    disabled={inviteSending}
                  >
                    {isInviteOpen ? 'Cancel' : 'Invite to Upgrade'}
                  </OutlineButton>
                )}
              </div>

              {isInviteOpen && (
                <div
                  style={{
                    background: colors.off,
                    borderTop: `1px solid ${colors.border}`,
                    padding: '14px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '11px',
                      color: colors.muted,
                      marginBottom: '8px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Send upgrade invite to {agent.agent_full_name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px', lineHeight: 1.5 }}>
                    The agent will receive an invitation to upgrade to{' '}
                    <strong>{meta.label}</strong> (${meta.price}/mo). They'll add payment in
                    their own dashboard — your broker subscription isn't affected.
                  </div>
                  <NavyInput
                    placeholder={`Optional message (e.g. "${meta.label} would help with your buyer search workflow")`}
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    style={{
                      background: colors.white,
                      color: colors.navy,
                      border: `1px solid ${colors.border}`,
                      marginBottom: '10px',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <OutlineButton
                      onClick={() => {
                        setShowInviteForm(null);
                        setInviteMessage('');
                      }}
                      disabled={inviteSending}
                    >
                      Cancel
                    </OutlineButton>
                    <GoldButton onClick={() => handleSendInvite(key)} disabled={inviteSending}>
                      {inviteSending ? 'Sending…' : 'Send Invitation'}
                    </GoldButton>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </SectionCard>
    </>
  );
};

// ---- Commission pane -------------------------------------------------------
// KOPS-465: loads commission plan templates from the brokerage (GET
// /api/commission-plans/templates/:brokerageId) and lets the broker
// assign one to the selected agent via POST /api/commission-plans/assign
// — same behavior as the legacy BrokerIntelligence CommissionPlans
// screen. Below the template selector we keep the manual overrides
// form so brokers can tune the splits/fees on a per-agent basis without
// a full plan assignment. Overrides ride along on the assign payload
// when a template is selected, or go through the direct PATCH path
// (onSave) when the broker just edits the splits.

interface CommissionPlanTemplate {
  id: string;
  name: string;
  description: string;
  baseAgentSplit: number;
  baseBrokerSplit: number;
  transactionFee: number;
  eoFee: number;
  monthlyFee: number;
  isDefault: boolean;
  agentCount: number;
}

const CommissionPane: React.FC<{
  agent: Agent;
  brokerageId?: string;
  saving: boolean;
  onSave: (payload: Record<string, any>) => Promise<boolean>;
  onRefresh: () => Promise<void>;
}> = ({ agent, brokerageId, saving, onSave, onRefresh }) => {
  const [templates, setTemplates] = useState<CommissionPlanTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  // currentTemplateId = the plan that's actually assigned to this agent
  // in the DB right now. selectedTemplateId starts equal to this on
  // load, and diverges only if the broker clicks a different template.
  // Keeping them separate lets us render a distinct "Current" badge
  // independent of whatever the broker has their cursor on.
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [assignResult, setAssignResult] = useState<string | null>(null);

  const [form, setForm] = useState({
    commission_split_agent: agent.commission_split_agent ?? 80,
    commission_split_broker: agent.commission_split_broker ?? 20,
    eo_fee: agent.eo_fee ?? 0,
    transaction_fee: agent.transaction_fee ?? 0,
    monthly_fee: agent.monthly_fee ?? 0,
  });

  useEffect(() => {
    setForm({
      commission_split_agent: agent.commission_split_agent ?? 80,
      commission_split_broker: agent.commission_split_broker ?? 20,
      eo_fee: agent.eo_fee ?? 0,
      transaction_fee: agent.transaction_fee ?? 0,
      monthly_fee: agent.monthly_fee ?? 0,
    });
    setSelectedTemplateId(null);
    setCurrentTemplateId(null);
    setAssignResult(null);
  }, [agent.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load brokerage commission plan templates + the agent's currently
  // active plan assignment in parallel. Auto-selecting the current plan
  // lets the broker see "which plan is this agent on today?" at a
  // glance, and makes re-assigning the same plan (or tweaking overrides)
  // a one-click operation instead of hunting for the right row.
  useEffect(() => {
    if (!brokerageId || !agent.id) return;
    let cancelled = false;
    (async () => {
      setTemplatesLoading(true);
      setTemplatesError(null);
      try {
        const [tplRes, currentRes] = await Promise.all([
          axiosInstance.get(`/commission-plans/templates/${brokerageId}`),
          axiosInstance
            .get(`/commission-plans/agent/${agent.id}/current`)
            .catch(() => ({ data: { success: true, currentPlan: null } })),
        ]);
        if (cancelled) return;

        const loadedTemplates: CommissionPlanTemplate[] =
          tplRes.data?.success && Array.isArray(tplRes.data.templates)
            ? tplRes.data.templates
            : [];
        setTemplates(loadedTemplates);

        const current = currentRes.data?.currentPlan;
        if (current?.templateId) {
          // Only auto-select if the template is still in the brokerage's
          // active template list — handles edge case where a template
          // was archived after assignment.
          const match = loadedTemplates.find((t) => t.id === current.templateId);
          if (match) {
            setCurrentTemplateId(current.templateId);
            setSelectedTemplateId(current.templateId);
            // Pre-fill the override form with whatever was actually
            // saved for this agent — overrides if present, template
            // base values otherwise. This way the broker sees the
            // effective splits/fees that are currently in force.
            setForm({
              commission_split_agent:
                current.overrideAgentSplit ?? match.baseAgentSplit,
              commission_split_broker:
                current.overrideBrokerSplit ?? match.baseBrokerSplit,
              eo_fee: current.overrideEoFee ?? match.eoFee,
              transaction_fee: current.overrideTransactionFee ?? match.transactionFee,
              monthly_fee: agent.monthly_fee ?? match.monthlyFee,
            });
          }
        }
      } catch (err: any) {
        if (cancelled) return;
        setTemplatesError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            'Could not load commission plans'
        );
        setTemplates([]);
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brokerageId, agent.id]);

  // When a template is picked, hydrate the manual form so the broker can
  // see the base splits/fees and optionally override before assigning.
  const handleTemplatePick = (tpl: CommissionPlanTemplate) => {
    setSelectedTemplateId(tpl.id);
    setForm({
      commission_split_agent: tpl.baseAgentSplit,
      commission_split_broker: tpl.baseBrokerSplit,
      eo_fee: tpl.eoFee,
      transaction_fee: tpl.transactionFee,
      monthly_fee: tpl.monthlyFee,
    });
  };

  const handleAssignPlan = async () => {
    if (!selectedTemplateId) return;
    setAssigning(true);
    setAssignResult(null);
    try {
      // Only pass overrides that differ from the selected template's
      // base values — keeps the plan history clean.
      const tpl = templates.find((t) => t.id === selectedTemplateId);
      const overrides: Record<string, any> = {};
      if (tpl) {
        if (form.commission_split_agent !== tpl.baseAgentSplit) {
          overrides.agentSplit = form.commission_split_agent;
        }
        if (form.commission_split_broker !== tpl.baseBrokerSplit) {
          overrides.brokerSplit = form.commission_split_broker;
        }
        if (form.transaction_fee !== tpl.transactionFee) {
          overrides.transactionFee = form.transaction_fee;
        }
        if (form.eo_fee !== tpl.eoFee) {
          overrides.eoFee = form.eo_fee;
        }
      }

      const res = await axiosInstance.post('/commission-plans/assign', {
        agentId: agent.id,
        templateId: selectedTemplateId,
        effectiveStartDate: new Date().toISOString().split('T')[0],
        overrides: Object.keys(overrides).length ? overrides : undefined,
        monthlyFee: form.monthly_fee,
        notes: null,
      });
      if (res.data?.success) {
        setAssignResult(`✅ Commission plan assigned to ${agent.agent_full_name || 'agent'}`);
        setCurrentTemplateId(selectedTemplateId);
        await onRefresh();
      } else {
        setAssignResult(`❌ ${res.data?.message || res.data?.error || 'Assign failed'}`);
      }
    } catch (err: any) {
      setAssignResult(
        `❌ ${err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Assign failed'}`
      );
    } finally {
      setAssigning(false);
      setTimeout(() => setAssignResult(null), 5000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <SectionCard title="Brokerage Commission Plans">
        <div style={{ fontSize: '12px', color: colors.muted, marginBottom: '12px', lineHeight: 1.5 }}>
          Select a plan from your brokerage's commission table. Pick one and
          the fields below will pre-fill from the template — edit the
          splits/fees if you need a per-agent override, then click{' '}
          <strong>Assign Plan</strong>.
        </div>

        {templatesLoading && (
          <div style={{ fontSize: '12px', color: colors.muted, padding: '12px 0' }}>
            Loading plans…
          </div>
        )}

        {templatesError && (
          <div
            style={{
              background: '#f8d7da',
              color: colors.red,
              padding: '10px 14px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '10px',
            }}
          >
            {templatesError}
          </div>
        )}

        {!templatesLoading && !templatesError && templates.length === 0 && (
          <div
            style={{
              fontSize: '12px',
              color: colors.muted,
              padding: '16px',
              background: colors.offBg,
              borderRadius: '6px',
              textAlign: 'center',
            }}
          >
            No commission plans defined for this brokerage yet. Create one in
            the Commission tab.
          </div>
        )}

        {templates.length > 0 && (
          <div style={{ display: 'grid', gap: '8px' }}>
            {templates.map((tpl) => {
              const isSelected = selectedTemplateId === tpl.id;
              const isCurrent = currentTemplateId === tpl.id;
              return (
                <div
                  key={tpl.id}
                  onClick={() => handleTemplatePick(tpl)}
                  style={{
                    padding: '12px 14px',
                    background: isSelected ? `${colors.gold}11` : colors.white,
                    border: `1px solid ${isSelected ? colors.gold : colors.border}`,
                    borderLeft: `3px solid ${isSelected ? colors.gold : colors.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: `2px solid ${isSelected ? colors.gold : colors.muted}`,
                        background: isSelected ? colors.gold : 'transparent',
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: colors.navy,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {tpl.name}
                        {isCurrent && (
                          <span
                            style={{
                              background: colors.green,
                              color: colors.white,
                              fontSize: '9px',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            ✓ Current
                          </span>
                        )}
                        {tpl.isDefault && !isCurrent && (
                          <span
                            style={{
                              background: `${colors.gold}22`,
                              color: colors.gold,
                              fontSize: '9px',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Default
                          </span>
                        )}
                        <span style={{ fontSize: '10px', color: colors.muted, fontWeight: 500 }}>
                          · {tpl.agentCount} agent{tpl.agentCount === 1 ? '' : 's'}
                        </span>
                      </div>
                      {tpl.description && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: colors.muted,
                            marginTop: '2px',
                          }}
                        >
                          {tpl.description}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: '11px',
                          color: colors.navy,
                          marginTop: '4px',
                          display: 'flex',
                          gap: '12px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span>
                          <strong>{tpl.baseAgentSplit}%</strong> / {tpl.baseBrokerSplit}%
                        </span>
                        <span>
                          Txn: <strong>${tpl.transactionFee}</strong>
                        </span>
                        <span>
                          E&amp;O: <strong>${tpl.eoFee}</strong>
                        </span>
                        <span>
                          Monthly: <strong>${tpl.monthlyFee}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {assignResult && (
          <div
            style={{
              background: assignResult.startsWith('✅') ? '#d4edda' : '#f8d7da',
              color: assignResult.startsWith('✅') ? colors.green : colors.red,
              padding: '10px 14px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              marginTop: '12px',
            }}
          >
            {assignResult}
          </div>
        )}

        {selectedTemplateId && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
            <GoldButton
              type="button"
              onClick={handleAssignPlan}
              disabled={assigning}
            >
              <FontAwesomeIcon icon={faFloppyDisk} />{' '}
              {assigning ? 'Assigning…' : 'Assign Plan to Agent'}
            </GoldButton>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Commission Splits">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <FormLabel>Agent Split (%)</FormLabel>
            <WhiteInput
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.commission_split_agent}
              onChange={(e) => setForm({ ...form, commission_split_agent: Number(e.target.value) })}
            />
          </div>
          <div>
            <FormLabel>Broker Split (%)</FormLabel>
            <WhiteInput
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.commission_split_broker}
              onChange={(e) => setForm({ ...form, commission_split_broker: Number(e.target.value) })}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Fees">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <div>
            <FormLabel>E&amp;O Fee ($)</FormLabel>
            <WhiteInput
              type="number"
              step="0.01"
              min="0"
              value={form.eo_fee}
              onChange={(e) => setForm({ ...form, eo_fee: Number(e.target.value) })}
            />
          </div>
          <div>
            <FormLabel>Transaction Fee ($)</FormLabel>
            <WhiteInput
              type="number"
              step="0.01"
              min="0"
              value={form.transaction_fee}
              onChange={(e) => setForm({ ...form, transaction_fee: Number(e.target.value) })}
            />
          </div>
          <div>
            <FormLabel>Monthly Fee ($)</FormLabel>
            <WhiteInput
              type="number"
              step="0.01"
              min="0"
              value={form.monthly_fee}
              onChange={(e) => setForm({ ...form, monthly_fee: Number(e.target.value) })}
            />
          </div>
        </div>
      </SectionCard>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <GoldButton type="submit" disabled={saving}>
          <FontAwesomeIcon icon={faFloppyDisk} /> {saving ? 'Saving…' : 'Save Commission'}
        </GoldButton>
      </div>
    </form>
  );
};

// ---- Activity pane ---------------------------------------------------------
// KOPS-465: five-section broker-facing view of everything that matters
// about an agent at a glance:
//   1. KPI strip  — YTD GCI, closings, avg days to close, days since active
//   2. Active transactions (upcoming closings highlighted)
//   3. Recent payouts (last 5 completed deals + commission math)
//   4. Commission plan change audit trail
//   5. Account timeline footer
//
// Single GET /broker/agents/:agentId/activity backs the whole pane so
// there's no N+1 request fan-out when switching between agents.

interface ActivityData {
  kpis: {
    ytd_gci: number;
    closings_ytd: number;
    avg_days_to_close: number | null;
    days_since_last_activity: number | null;
  };
  activeTransactions: Array<{
    id: string;
    file_number: string | null;
    stage: string;
    client_name: string | null;
    property_address: string | null;
    type: string | null;
    sale_price: number | null;
    est_agent_commission: number | null;
    updated_at: string | null;
    created_at: string | null;
  }>;
  recentPayouts: Array<{
    id: string;
    file_number: string | null;
    client_name: string | null;
    property_address: string | null;
    closed_at: string | null;
    sale_price: number;
    gross_commission: number;
    agent_gross_commission: number;
    agent_net_commission: number;
    broker_net_commission: number;
  }>;
  planChanges: Array<{
    id: string;
    change_type: string;
    change_reason: string | null;
    effective_date: string | null;
    changed_at: string | null;
    old_template_name: string | null;
    new_template_name: string | null;
    changed_by_name: string | null;
  }>;
  timeline: {
    created_at: string | null;
    joined_at: string | null;
    effective_date: string | null;
    last_active: string | null;
    last_closed_at: string | null;
    member_status: string | null;
  };
}

const STAGE_META: Record<string, { label: string; color: string }> = {
  pre_contract: { label: 'Pre-contract', color: colors.muted },
  showing: { label: 'Showing', color: colors.blue },
  under_contract: { label: 'Under contract', color: colors.gold },
  closing: { label: 'Closing', color: colors.green },
  completed: { label: 'Completed', color: colors.green },
  cancelled: { label: 'Cancelled', color: colors.red },
};

const fmtMoney = (n: number | null | undefined) =>
  n == null
    ? '—'
    : `$${Math.round(n).toLocaleString('en-US')}`;

const fmtDate = (s: string | null | undefined) => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ActivityPane: React.FC<{ agent: Agent }> = ({ agent }) => {
  const [data, setData] = useState<ActivityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agent.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get(`/broker/agents/${agent.id}/activity`);
        if (cancelled) return;
        if (res.data?.success) {
          setData(res.data);
        } else {
          setError(res.data?.error || 'Failed to load activity');
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to load activity'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agent.id]);

  if (loading && !data) {
    return (
      <SectionCard title="Loading…">
        <div style={{ textAlign: 'center', padding: '32px 0', color: colors.muted, fontSize: '13px' }}>
          Fetching activity for {agent.agent_full_name}…
        </div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Activity">
        <div style={{ color: colors.red, fontSize: '13px', padding: '12px' }}>❌ {error}</div>
      </SectionCard>
    );
  }

  if (!data) return null;

  const { kpis, activeTransactions, recentPayouts, planChanges, timeline } = data;

  return (
    <>
      {/* ---- 1. KPI strip ---- */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '10px',
          marginBottom: '12px',
        }}
      >
        <MiniKPI label="YTD GCI" value={fmtMoney(kpis.ytd_gci)} color={colors.gold} />
        <MiniKPI
          label="Closings YTD"
          value={String(kpis.closings_ytd)}
          color={colors.green}
        />
        <MiniKPI
          label="Avg Days to Close"
          value={kpis.avg_days_to_close != null ? `${kpis.avg_days_to_close}d` : '—'}
          color={colors.blue}
        />
        <MiniKPI
          label="Last Active"
          value={
            kpis.days_since_last_activity == null
              ? '—'
              : kpis.days_since_last_activity === 0
              ? 'Today'
              : `${kpis.days_since_last_activity}d ago`
          }
          color={
            kpis.days_since_last_activity != null && kpis.days_since_last_activity > 30
              ? colors.red
              : colors.muted
          }
        />
      </div>

      {/* ---- 2. Active transactions ---- */}
      <SectionCard title={`Active Transactions (${activeTransactions.length})`}>
        {activeTransactions.length === 0 ? (
          <div style={{ color: colors.muted, fontSize: '12px', padding: '8px 0' }}>
            No open transactions right now.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {activeTransactions.map((t) => {
              const meta = STAGE_META[t.stage] || { label: t.stage, color: colors.muted };
              return (
                <div
                  key={t.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px',
                    background: colors.white,
                    border: `1px solid ${colors.border}`,
                    borderLeft: `3px solid ${meta.color}`,
                    borderRadius: '6px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: colors.navy,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.property_address || t.client_name || t.file_number || 'Untitled transaction'}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>
                      {t.file_number ? `#${t.file_number} · ` : ''}
                      {t.client_name || '—'}
                      {t.sale_price ? ` · ${fmtMoney(t.sale_price)}` : ''}
                    </div>
                  </div>
                  <span
                    style={{
                      background: `${meta.color}22`,
                      color: meta.color,
                      fontSize: '9px',
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: '4px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      flexShrink: 0,
                    }}
                  >
                    {meta.label}
                  </span>
                  {t.est_agent_commission != null && (
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: colors.gold,
                        flexShrink: 0,
                      }}
                    >
                      {fmtMoney(t.est_agent_commission)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* ---- 3. Recent payouts ---- */}
      <SectionCard title={`Recent Payouts (${recentPayouts.length})`}>
        {recentPayouts.length === 0 ? (
          <div style={{ color: colors.muted, fontSize: '12px', padding: '8px 0' }}>
            No closed transactions yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '8px' }}>
            {recentPayouts.map((p) => (
              <div
                key={p.id}
                style={{
                  padding: '10px 12px',
                  background: colors.white,
                  border: `1px solid ${colors.border}`,
                  borderLeft: `3px solid ${colors.green}`,
                  borderRadius: '6px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: '12px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: colors.navy,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.property_address || p.client_name || p.file_number || 'Closed deal'}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>
                      {fmtDate(p.closed_at)}
                      {p.file_number ? ` · #${p.file_number}` : ''}
                      {p.sale_price ? ` · Sold ${fmtMoney(p.sale_price)}` : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: colors.gold }}>
                    {fmtMoney(p.agent_net_commission || p.agent_gross_commission)}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    fontSize: '10px',
                    color: colors.muted,
                    marginTop: '6px',
                    paddingTop: '6px',
                    borderTop: `1px dashed ${colors.border}`,
                  }}
                >
                  <span>Gross: {fmtMoney(p.gross_commission)}</span>
                  <span>Agent gross: {fmtMoney(p.agent_gross_commission)}</span>
                  <span>Broker net: {fmtMoney(p.broker_net_commission)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* ---- 4. Plan change history ---- */}
      <SectionCard title={`Commission Plan History (${planChanges.length})`}>
        {planChanges.length === 0 ? (
          <div style={{ color: colors.muted, fontSize: '12px', padding: '8px 0' }}>
            No plan changes logged.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '6px' }}>
            {planChanges.map((c) => (
              <div
                key={c.id}
                style={{
                  padding: '8px 12px',
                  background: colors.white,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  fontSize: '12px',
                }}
              >
                <div style={{ color: colors.navy, fontWeight: 600 }}>
                  {c.change_type === 'created' ? '➕ Assigned' : '🔄 Updated'}{' '}
                  <span style={{ color: colors.gold }}>
                    {c.new_template_name || 'plan'}
                  </span>
                  {c.old_template_name && c.change_type !== 'created' && (
                    <span style={{ color: colors.muted, fontWeight: 400 }}>
                      {' '}
                      (was {c.old_template_name})
                    </span>
                  )}
                </div>
                <div style={{ color: colors.muted, fontSize: '11px', marginTop: '2px' }}>
                  {fmtDate(c.changed_at)}
                  {c.changed_by_name ? ` · by ${c.changed_by_name}` : ''}
                  {c.change_reason ? ` · ${c.change_reason}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* ---- 5. Account timeline footer ---- */}
      <SectionCard title="Account Timeline">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '8px',
            fontSize: '11px',
          }}
        >
          <TimelineItem label="Invited" value={fmtDate(timeline.created_at)} />
          <TimelineItem label="Joined" value={fmtDate(timeline.joined_at)} />
          <TimelineItem label="Effective" value={fmtDate(timeline.effective_date)} />
          <TimelineItem label="Last Active" value={fmtDate(timeline.last_active)} />
          <TimelineItem label="Last Closing" value={fmtDate(timeline.last_closed_at)} />
          <TimelineItem
            label="Status"
            value={timeline.member_status || '—'}
            valueColor={
              timeline.member_status === 'active'
                ? colors.green
                : timeline.member_status === 'pending'
                ? colors.orange
                : colors.muted
            }
          />
        </div>
      </SectionCard>
    </>
  );
};

const MiniKPI: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: '6px',
      padding: '10px 12px',
    }}
  >
    <div style={{ fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </div>
    <div style={{ fontSize: '18px', fontWeight: 700, color: colors.navy, marginTop: '2px' }}>
      {value}
    </div>
  </div>
);

const TimelineItem: React.FC<{ label: string; value: string; valueColor?: string }> = ({
  label,
  value,
  valueColor,
}) => (
  <div>
    <div
      style={{
        fontSize: '9px',
        color: colors.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: '12px', color: valueColor || colors.navy, fontWeight: 600, marginTop: '2px' }}>
      {value}
    </div>
  </div>
);

// ===========================================================================
// Small reusable bits
// ===========================================================================

const TeamPlanChip: React.FC<{ plan: TeamPlanKey; compact?: boolean; alwaysShow?: boolean }> = ({
  plan,
  compact,
  alwaysShow,
}) => {
  const meta = TEAM_PLAN_META[plan];
  const label = compact ? meta.shortLabel : meta.label;
  const freeSuffix = plan === 'team-member' && alwaysShow ? ' · FREE' : '';
  return (
    <span
      title={meta.tagline}
      style={{
        background: `${meta.color}22`,
        color: meta.color,
        fontSize: '9px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      <FontAwesomeIcon icon={meta.icon} style={{ fontSize: '9px' }} />
      {label}
      {freeSuffix}
    </span>
  );
};

const ActiveBadge: React.FC<{ active: boolean; status?: string }> = ({ active, status }) => {
  const isPending = status === 'pending';
  const color = active ? colors.green : isPending ? colors.orange : colors.muted;
  const label = active ? 'Active' : isPending ? 'Pending' : status || 'Inactive';
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </span>
  );
};

const EmptyDetailState: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '48px',
      color: colors.muted,
    }}
  >
    <FontAwesomeIcon icon={faUser} style={{ fontSize: '48px', marginBottom: '12px', color: colors.gold3 }} />
    <div style={{ fontSize: '15px', fontWeight: 600, color: colors.navy }}>Select an agent</div>
    <div style={{ fontSize: '12px', marginTop: '4px' }}>
      Pick a team member to view details, edit records, or change plan
    </div>
  </div>
);

export default AgentsTab;
